import { useState } from "react";
import { ChevronLeft, ChevronRight, PackageOpen, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import type { Order } from "@aurelia/types";
import { EmptyState, PriceTag, Skeleton, toast } from "@aurelia/ui";
import { useInitiateSafepay, useMyOrders } from "../../features/orders/queries";
import { useStoreConfig } from "../../features/config/queries";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { PaymentInstructions, hasPaymentDetails } from "../../components/payment/PaymentInstructions";
import { OrderProductRow } from "../../components/orders/OrderProductRow";
import { OrderReturnControl } from "../../components/orders/OrderReturnControl";
import { OrderStatusBadges } from "../../components/orders/OrderStatusBadges";
import { TrackingNumber } from "../../components/orders/TrackingNumber";
import { formatOrderDate, orderTotal } from "../../components/orders/orderPresentation";

const PAGE_SIZE = 10;

function OrdersSkeleton() {
  return (
    <div className="flex flex-col gap-5" aria-label="Loading orders">
      {Array.from({ length: 2 }).map((_, index) => (
        <div key={index} className="overflow-hidden rounded-2xl border border-border bg-surface-raised p-4 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2"><Skeleton className="h-5 w-40" /><Skeleton className="h-4 w-28" /></div>
            <Skeleton className="h-8 w-24" />
          </div>
          <div className="mt-4 flex gap-2"><Skeleton className="h-7 w-24 rounded-full" /><Skeleton className="h-7 w-20 rounded-full" /></div>
          <div className="mt-5 flex gap-3 border-t border-border pt-4"><Skeleton className="h-[72px] w-[72px] shrink-0" /><div className="flex-1 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-4 w-1/2" /><Skeleton className="h-4 w-1/3" /></div></div>
        </div>
      ))}
    </div>
  );
}

function OrderCard({ order }: { order: Order }) {
  const { data: config } = useStoreConfig();
  const initiateSafepay = useInitiateSafepay();

  const retryPayment = () => {
    initiateSafepay.mutate(order.id, {
      onSuccess: (result) => window.location.assign(result.checkoutUrl),
      onError: () => toast("Safepay couldn't be opened. Please try again.", "danger")
    });
  };

  return (
    <article className="overflow-hidden rounded-2xl border border-border bg-surface-raised shadow-soft">
      <header className="border-b border-border bg-surface/50 p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">Order</p>
            <h2 className="mt-1 break-all text-lg font-semibold text-ink sm:text-xl">{order.orderNumber}</h2>
            <p className="mt-1 text-sm text-ink-muted">Placed {formatOrderDate(order.createdAt)}</p>
          </div>
          <div className="sm:text-right">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">Order total</p>
            <PriceTag cents={orderTotal(order)} className="mt-1 text-xl sm:text-2xl" />
          </div>
        </div>
        <div className="mt-4"><OrderStatusBadges order={order} /></div>
      </header>

      <div className="p-4 sm:p-6">
        <div className="rounded-xl bg-surface px-4 py-3">
          <TrackingNumber trackingNumber={order.trackingNumber} status={order.status} />
        </div>

        <div className="mt-2 divide-y divide-border" aria-label={`Products in ${order.orderNumber}`}>
          {order.items.map((item) => (
            <OrderProductRow key={item.id} item={item}>
              <OrderReturnControl order={order} item={item} />
            </OrderProductRow>
          ))}
        </div>

        {order.status === "PENDING" && order.paymentMethod === "ADVANCE_COD" && hasPaymentDetails(config?.payment) && (
          <div className="mt-4"><PaymentInstructions payment={config!.payment} advanceAmount={order.advanceAmount} /></div>
        )}

        <footer className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-ink-muted">{order.items.length} {order.items.length === 1 ? "product" : "products"}</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            {order.paymentMethod === "SAFEPAY" && order.paymentStatus !== "PAID" && order.status !== "CANCELLED" && (
              <button type="button" disabled={initiateSafepay.isPending} onClick={retryPayment} className="inline-flex h-11 items-center justify-center rounded-full border border-accent px-5 text-sm font-medium text-accent-strong transition-colors hover:bg-accent-soft disabled:opacity-40">
                {initiateSafepay.isPending ? "Opening Safepay…" : "Retry payment"}
              </button>
            )}
            <Link to={`/account/orders/${order.id}`} className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-ink px-5 text-sm font-medium text-canvas transition-colors hover:bg-accent hover:text-ink dark:bg-accent dark:text-ink">
              View order details <ChevronRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </footer>
      </div>
    </article>
  );
}

export function OrdersPage() {
  useDocumentMeta("Order History", "Track and manage your past orders.", undefined, undefined, { noindex: true });
  const [page, setPage] = useState(1);
  const orders = useMyOrders(page, PAGE_SIZE);

  if (orders.isLoading) return <OrdersSkeleton />;

  if (orders.isError) {
    return <EmptyState tone="error" title="We couldn't load your orders" description="Please check your connection and try again." action={<button type="button" onClick={() => orders.refetch()} className="mt-1 inline-flex h-11 items-center gap-2 rounded-full bg-ink px-5 text-sm font-medium text-canvas dark:bg-accent dark:text-ink"><RefreshCw size={16} aria-hidden="true" /> Retry</button>} />;
  }

  if (!orders.data || orders.data.items.length === 0) {
    return (
      <EmptyState
        title={page === 1 ? "No orders yet" : "No more orders"}
        description={page === 1 ? "Products you purchase will appear here with payment and delivery updates." : "Return to your latest orders."}
        action={page === 1 ? <Link to="/" className="mt-1 inline-flex h-11 items-center gap-2 rounded-full bg-ink px-6 text-sm font-medium text-canvas dark:bg-accent dark:text-ink"><PackageOpen size={17} aria-hidden="true" /> Continue shopping</Link> : <button type="button" onClick={() => setPage(1)} className="mt-1 h-11 rounded-full bg-ink px-6 text-sm font-medium text-canvas dark:bg-accent dark:text-ink">Latest orders</button>}
      />
    );
  }

  const pageCount = Math.ceil(orders.data.total / orders.data.pageSize);

  return (
    <div className="flex min-w-0 flex-col gap-5 sm:gap-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div><h1 className="text-2xl font-semibold sm:text-3xl">Your orders</h1><p className="mt-1 text-sm text-ink-muted">Review products, payment status, and delivery progress.</p></div>
        <p className="text-sm text-ink-muted">{orders.data.total} {orders.data.total === 1 ? "order" : "orders"}</p>
      </div>
      {orders.data.items.map((order) => <OrderCard key={order.id} order={order} />)}
      {pageCount > 1 && (
        <nav className="flex items-center justify-between gap-3 pt-2" aria-label="Order history pages">
          <button type="button" disabled={page <= 1 || orders.isFetching} onClick={() => setPage((value) => Math.max(1, value - 1))} className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-4 text-sm font-medium disabled:opacity-40"><ChevronLeft size={16} aria-hidden="true" /> Previous</button>
          <span className="text-sm text-ink-muted">Page {page} of {pageCount}</span>
          <button type="button" disabled={page >= pageCount || orders.isFetching} onClick={() => setPage((value) => Math.min(pageCount, value + 1))} className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-4 text-sm font-medium disabled:opacity-40">Next <ChevronRight size={16} aria-hidden="true" /></button>
        </nav>
      )}
    </div>
  );
}
